import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const MAX_SIDE = 1200;
const MAX_BYTES = 600_000;
const OUTPUT_MIME = "image/jpeg";
const LIMIT = 12;

function resizeToDataURL(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      try {
        const ratio = Math.min(MAX_SIDE / img.width, MAX_SIDE / img.height, 1);
        const w = Math.max(1, Math.round(img.width * ratio));
        const h = Math.max(1, Math.round(img.height * ratio));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        // Iterate quality down until under MAX_BYTES
        let q = 0.85;
        let url2 = canvas.toDataURL(OUTPUT_MIME, q);
        while (url2.length > MAX_BYTES * 1.37 && q > 0.4) {
          q -= 0.1;
          url2 = canvas.toDataURL(OUTPUT_MIME, q);
        }
        URL.revokeObjectURL(url);
        resolve(url2);
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image"));
    };
    img.src = url;
  });
}

export default function GalleryUploader({ images, onChange }) {
  const ref = useRef(null);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // {data_url}

  const pick = () => ref.current?.click();

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please pick a JPG, PNG, or WebP image");
      return;
    }
    setBusy(true);
    try {
      const data_url = await resizeToDataURL(f);
      setPending({ data_url });
    } catch (err) {
      toast.error(err.message || "Could not process image");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const saveImage = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const { data } = await api.post("/me/business/gallery", {
        data_url: pending.data_url,
        caption,
      });
      toast.success("Photo added to your gallery");
      onChange([...(images || []), data.image]);
      setPending(null);
      setCaption("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const removeImage = async (img) => {
    try {
      await api.delete(`/me/business/gallery/${img.id}`);
      onChange((images || []).filter((i) => i.id !== img.id));
      toast.success("Removed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const atLimit = (images || []).length >= LIMIT;

  return (
    <div className="space-y-5">
      {(images || []).length > 0 && (
        <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="gallery-grid">
          {images.map((img) => (
            <li
              key={img.id}
              className="group relative aspect-square overflow-hidden rounded-[35px] border border-border bg-gray-50"
              data-testid={`gallery-item-${img.id}`}
            >
              <img src={img.data_url} alt={img.caption || "Work photo"} className="h-full w-full object-cover" />
              {img.caption && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                  <p className="text-[11px] text-white truncate">{img.caption}</p>
                </div>
              )}
              <button
                type="button"
                onClick={() => removeImage(img)}
                className="absolute top-1.5 right-1.5 h-7 w-7 inline-flex items-center justify-center rounded-md bg-white/85 backdrop-blur text-red-600 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
                aria-label="Remove image"
                data-testid={`gallery-remove-${img.id}`}
              >
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {!atLimit && (
        <div className="border border-dashed border-border rounded-md p-4 bg-gray-50/50">
          {!pending ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <input
                ref={ref}
                type="file"
                accept="image/*"
                onChange={handleFile}
                className="hidden"
                data-testid="gallery-file-input"
              />
              <Button
                type="button"
                onClick={pick}
                disabled={busy}
                variant="outline"
                className="rounded-[35px] shadow-sm hover:shadow-md"
                data-testid="gallery-pick-button"
              >
                <ImagePlus size={14} className="mr-1" />
                {busy ? "Processing…" : "Add a photo"}
              </Button>
              <p className="text-xs text-gray-500">
                {(images || []).length} / {LIMIT} photos — JPG/PNG/WebP, automatically resized.
              </p>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <img
                src={pending.data_url}
                alt="Preview"
                className="h-28 w-28 rounded-[35px] object-cover border border-border bg-white"
              />
              <div className="flex-1 w-full">
                <Input
                  value={caption}
                  onChange={(e) => setCaption(e.target.value.slice(0, 200))}
                  placeholder="Optional caption (e.g. 'Backyard pool install in Berkeley')"
                  className="rounded-md"
                  data-testid="gallery-caption-input"
                />
                <div className="mt-2 flex gap-2">
                  <Button
                    type="button"
                    onClick={saveImage}
                    disabled={busy}
                    className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]"
                    data-testid="gallery-save-button"
                  >
                    <Upload size={14} className="mr-1" />
                    {busy ? "Uploading…" : "Save to gallery"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="rounded-[35px]"
                    onClick={() => {
                      setPending(null);
                      setCaption("");
                    }}
                  >
                    Discard
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {atLimit && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          Gallery is full ({LIMIT} photos). Remove one to add another.
        </p>
      )}
    </div>
  );
}
